import { activeBeat, type BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { onStageLayoutAdjustments } from "../ActiveLocationGroup/layout";
import { nearLocationPlacements } from "../LocationLayout/near";
import { allLocationRuns } from "../LocationRun/catalog";
import {
  activeLocationRun,
  buildLocationRuns,
  locationPathOf,
  type LocationRun,
} from "../LocationRun/runs";
import { updateLocationCastDebugFrames } from "../../board/ApplyMarkers/debugFrames";
import { activeLocationGroupsAtPlayhead } from "..";
import {
  isLocationRunVisible,
  locationVisibilityFromGroups,
} from "./markerGroup";
import { filterLocationVisibilityAtPlayhead } from "../LocationVisibility/relevance";
import { locationStackStagePosition } from "./stagePosition";
import type { TimelineViewTransform } from "../../view/ViewTransform/transform";
import { paintLocationBoxElements } from "./applyDom";

export interface LocationBoxLayoutOptions {
  boardModelDebug?: boolean;
}

/** DOM nodes for every site and part marker parked along the rail. */
export function locationRunsForMount(layout: BookTimelineLayout): LocationRun[] {
  return allLocationRuns(layout);
}

/** Place site stacks and nested parts at fixed rail positions. */
export function applyLocationBoxLayout(
  root: HTMLElement,
  layout: BookTimelineLayout,
  playheadMs: number,
  viewportWidth: number,
  viewportHeight: number,
  view: TimelineViewTransform,
  options: LocationBoxLayoutOptions = {},
): void {
  void viewportHeight;
  void view; // camera zoom/pan only on the stage wrapper; not visibility
  const boardModelDebug = options.boardModelDebug ?? false;
  const runs = allLocationRuns(layout);
  const placements = nearLocationPlacements(layout, playheadMs, viewportWidth);
  const beat = activeBeat(layout, playheadMs);
  const runById = new Map(runs.map((run) => [run.runId, run]));
  const sceneRuns = buildLocationRuns(layout.beats);
  const activeRun = activeLocationRun(sceneRuns, playheadMs);
  const activePath = beat !== undefined ? locationPathOf(beat) : [];
  const locationGroups = activeLocationGroupsAtPlayhead(layout, playheadMs);
  const visibility = filterLocationVisibilityAtPlayhead(
    layout,
    playheadMs,
    locationVisibilityFromGroups(locationGroups),
  );
  const stageByRunId = new Map<string, { anchorX: number; topY: number }>();
  for (const placement of placements) {
    const run = runById.get(placement.runId);
    if (run === undefined) {
      continue;
    }
    if (!isLocationRunVisible(visibility, run.runId)) {
      continue;
    }
    stageByRunId.set(
      run.runId,
      locationStackStagePosition(
        layout,
        run,
        placement,
        playheadMs,
        viewportWidth,
      ),
    );
  }
  const onStageLayout = onStageLayoutAdjustments(
    locationGroups,
    stageByRunId,
    layout,
    playheadMs,
  );
  paintLocationBoxElements(
    root,
    layout,
    playheadMs,
    placements,
    runById,
    visibility,
    stageByRunId,
    onStageLayout,
    locationGroups,
    activePath,
    activeRun?.runId,
  );
  updateLocationCastDebugFrames(
    root,
    layout,
    playheadMs,
    boardModelDebug,
    locationGroups,
  );
}
